import { Component, EventEmitter, Input, Output, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NzModalModule } from 'ng-zorro-antd/modal';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzTabsModule } from 'ng-zorro-antd/tabs';
import { NzAlertModule } from 'ng-zorro-antd/alert';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { AuthService } from '../../core/services/auth.service';

@Component({
  selector: 'app-auth-modal',
  standalone: true,
  imports: [FormsModule, NzModalModule, NzButtonModule, NzInputModule, NzIconModule, NzTabsModule, NzAlertModule, NzSpinModule],
  templateUrl: './auth-modal.component.html',
})
export class AuthModalComponent {
  @Input() visible = false;
  @Input() sessionExpiry = false;
  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() authSuccess = new EventEmitter<void>();
  @Output() dismissed = new EventEmitter<void>();

  private readonly auth = inject(AuthService);

  readonly activeTabIndex = signal(0);
  readonly error     = signal<string | null>(null);
  readonly loading   = signal(false);
  readonly showVerify = signal(false);
  readonly verifyPurpose = signal<'login' | 'register'>('login');
  readonly verifyEmail = signal('');
  readonly resending = signal(false);

  showLoginPwd    = false;
  showRegPwd      = false;
  showRegConfirm  = false;

  loginEmail    = '';
  loginPassword = '';
  regEmail      = '';
  regPassword   = '';
  regConfirm    = '';
  verifyCode    = '';

  private succeededBeforeClose = false;

  setTab(index: number): void {
    this.activeTabIndex.set(index);
    this.error.set(null);
  }

  close(): void {
    this.showVerify.set(false);
    this.verifyCode = '';
    this.error.set(null);
    if (!this.succeededBeforeClose) {
      this.dismissed.emit();
    }
    this.succeededBeforeClose = false;
    this.visibleChange.emit(false);
  }

  login(): void {
    if (!this.loginEmail || !this.loginPassword) { this.error.set('Please enter your email and password.'); return; }
    this.loading.set(true);
    this.error.set(null);
    this.auth.login(this.loginEmail, this.loginPassword).subscribe({
      next: res => {
        this.loading.set(false);
        if (res.needsVerification) {
          this.verifyEmail.set(res.email ?? this.loginEmail);
          this.verifyPurpose.set('login');
          this.showVerify.set(true);
        } else if (res.success) {
          this.succeededBeforeClose = true;
          this.authSuccess.emit();
          this.close();
        } else {
          this.error.set(res.error ?? 'Login failed.');
        }
      },
      error: (err) => { this.loading.set(false); this.error.set(err?.error?.error ?? 'Login failed. Please try again.'); },
    });
  }

  register(): void {
    if (this.regPassword !== this.regConfirm) { this.error.set('Passwords do not match.'); return; }
    if (!this.regEmail || !this.regPassword) { this.error.set('Please fill in all fields.'); return; }
    if (!this.regEmail.includes('@') || !this.regEmail.includes('.')) { this.error.set('Please enter a valid email address.'); return; }
    if (this.regPassword.length < 8) { this.error.set('Password must be at least 8 characters.'); return; }
    this.loading.set(true);
    this.error.set(null);
    this.auth.register(this.regEmail, this.regPassword).subscribe({
      next: res => {
        this.loading.set(false);
        if (res.needsVerification) {
          this.verifyEmail.set(res.email ?? this.regEmail);
          this.verifyPurpose.set('register');
          this.showVerify.set(true);
        } else if (res.success) {
          this.succeededBeforeClose = true;
          this.authSuccess.emit();
          this.close();
        } else {
          this.error.set(res.error ?? 'Registration failed.');
        }
      },
      error: (err) => { this.loading.set(false); this.error.set(err?.error?.error ?? 'Registration failed. Please try again.'); },
    });
  }

  submitVerification(): void {
    // Strip everything that isn't a digit — handles copy-paste with spaces,
    // dashes, non-breaking spaces, or other invisible characters from email clients.
    this.verifyCode = this.verifyCode.replace(/\D/g, '');
    if (!this.verifyCode || this.verifyCode.length !== 6) {
      this.error.set('Please enter the 6-digit code.');
      return;
    }
    this.loading.set(true);
    this.error.set(null);
    this.auth.verify(this.verifyEmail(), this.verifyCode, this.verifyPurpose()).subscribe({
      next: res => {
        this.loading.set(false);
        if (res.success) {
          this.succeededBeforeClose = true;
          this.authSuccess.emit();
          this.close();
        } else {
          this.error.set(res.error ?? 'Verification failed.');
        }
      },
      error: (err) => {
        this.loading.set(false);
        this.error.set(err.error?.error ?? 'Invalid or expired code.');
      },
    });
  }

  resendCode(): void {
    this.resending.set(true);
    this.auth.resendCode(this.verifyEmail(), this.verifyPurpose()).subscribe({
      next: () => { this.resending.set(false); },
      error: () => { this.resending.set(false); },
    });
  }
}
